import { Button } from '@crowdex/ui/components/button';
import { SegmentedControl, SegmentedControlItem } from '@crowdex/ui/components/segmented-control';
import type { PullRequestSummary } from '@crowdex/workspace-protocol';
import {
  type PullRequestCockpit,
  type PullRequestMutation,
  type PullRequestReviewThread,
  visiblePullRequestFeedback,
} from '@crowdex/workspace-protocol/pull-request-cockpit';
import { Check, FileCode2, GitPullRequest, Undo2 } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { DesktopGitDiff } from '@/components/desktop-git-diff';
import { PanelEmpty } from '@/components/desktop-panel-primitives';
import {
  CreatePullRequestDialog,
  type CreatePullRequestInput,
  type PullRequestTextGenerator,
} from '@/features/pull-request/create-pull-request-dialog';
import type { ConversationFiles, ReviewScope } from '@/lib/desktop-shell/store';
import { useDesktopShellStore } from '@/lib/desktop-shell/store-context';
import { parseReviewDiff } from '@/lib/review-diff';
import {
  conversationPaths,
  diffForPaths,
  discardConfirmation,
  type ReviewSource,
  statusLinesForPaths,
} from '@/lib/side-panel/review-summary';

/** File-level git action a reviewer can trigger from the changes diff. */
export type ReviewFileAction = (path: string, action: 'stage' | 'unstage' | 'revert') => void;

type ReviewInput = {
  reviewDiffOverride: string | null;
  reviewError: string | null;
  reviewSource: ReviewSource;
  statusLines: readonly string[];
  diffText: string;
  /**
   * The uncommitted changes of the conversation's files only (`conversation`, with `files`), or
   * of the whole folder: other conversations may be working in it.
   */
  scope?: ReviewScope;
  files?: ConversationFiles;
};

/** Whether Review's uncommitted changes are the conversation's own files only. */
function conversationScoped(input: ReviewInput): boolean {
  return (
    input.reviewDiffOverride === null &&
    input.reviewSource === 'changes' &&
    input.scope === 'conversation' &&
    input.files !== undefined
  );
}

/**
 * What Review shows: a diff, an error, a clean tree or no text diff. The one source of its view
 * and of the count beside its tab.
 */
function reviewContent(
  input: ReviewInput,
):
  | { kind: 'override' | 'diff'; diff: string }
  | { kind: 'error'; error: string }
  | { kind: 'clean' }
  | { kind: 'empty' } {
  const { reviewDiffOverride, reviewError, reviewSource } = input;
  let { statusLines, diffText } = input;
  if (conversationScoped(input) && input.files !== undefined) {
    const paths = conversationPaths(input.files);
    statusLines = statusLinesForPaths(statusLines, paths);
    diffText = diffForPaths(diffText, paths);
  }
  if (reviewDiffOverride !== null) return { kind: 'override', diff: reviewDiffOverride };
  if (reviewError !== null) return { kind: 'error', error: reviewError };
  if (reviewSource === 'changes' && statusLines.length === 0) return { kind: 'clean' };
  if (diffText.length > 0) return { kind: 'diff', diff: diffText };
  return { kind: 'empty' };
}

/** The files Review lists, as its tab counts them (none for an error or a clean tree). */
export function reviewFileCount(input: ReviewInput): number {
  const content = reviewContent(input);
  if (content.kind === 'override' || content.kind === 'diff')
    return parseReviewDiff(content.diff).length;
  if (content.kind === 'empty' && input.reviewSource === 'changes')
    return conversationScoped(input) && input.files !== undefined
      ? statusLinesForPaths(input.statusLines, conversationPaths(input.files)).length
      : input.statusLines.length;
  return 0;
}

/**
 * The Review tab body: renders a git diff (either an override diff supplied by
 * the shell, or the live working-tree / pull-request diff), or one of several
 * empty / error states — clean tree, no PR diff, or a git error. The outer
 * activation guard lives in the parent `SidePanel`.
 */
export function ReviewView({
  reviewDiffOverride,
  reviewError,
  reviewSource,
  statusLines,
  diffText,
  reviewLabel,
  pullRequest,
  pullRequestCockpit,
  onMutatePullRequest,
  onSubmitReview,
  onToggleSource,
  onFileAction,
  columns = false,
  generateGitText,
  onOpenPullRequest,
  onPullRequestChange,
}: {
  /** Review in a docked panel: the files beside the diff, and its footer (D24). */
  columns?: boolean;
  generateGitText?: PullRequestTextGenerator | undefined;
  onOpenPullRequest?: () => void;
  onPullRequestChange?: () => Promise<void>;
  reviewDiffOverride: string | null;
  reviewError: string | null;
  reviewSource: ReviewSource;
  statusLines: readonly string[];
  diffText: string;
  reviewLabel: string;
  pullRequest: PullRequestSummary | null;
  pullRequestCockpit: PullRequestCockpit | null;
  onMutatePullRequest: (mutation: PullRequestMutation) => Promise<void>;
  onSubmitReview: (review: string) => void;
  onToggleSource: () => void;
  onFileAction: ReviewFileAction;
}) {
  const toggleSource = pullRequest === null ? undefined : onToggleSource;
  const conversationFiles = useDesktopShellStore((state) => state.conversationFiles);
  const scope = useDesktopShellStore((state) => state.reviewScope);
  const setScope = useDesktopShellStore((state) => state.setReviewScope);
  const content = reviewContent({
    reviewDiffOverride,
    reviewError,
    reviewSource,
    statusLines,
    diffText,
    scope,
    files: conversationFiles,
  });
  const changedCount = reviewFileCount({
    reviewDiffOverride,
    reviewError,
    reviewSource,
    statusLines,
    diffText,
    scope,
    files: conversationFiles,
  });
  const scopable = reviewDiffOverride === null && reviewSource === 'changes';
  const scopedToConversation = scopable && scope === 'conversation';
  // Discarding asks first, more plainly for a file another conversation may be working on.
  const fileAction: ReviewFileAction = (path, action) => {
    if (action === 'revert' && !window.confirm(discardConfirmation(path, conversationFiles)))
      return;
    onFileAction(path, action);
  };
  const gitStatus = useDesktopShellStore((state) => state.gitStatus);
  const workspace = useDesktopShellStore((state) => state.workspace);
  const cloud = useDesktopShellStore((state) => state.executionMode === 'cloud');
  const draftPullRequests = useDesktopShellStore((state) => state.appSettings.draftPullRequests);
  const [creating, setCreating] = useState<CreatePullRequestInput | null>(null);
  const canCreatePullRequest =
    !cloud &&
    workspace !== null &&
    gitStatus !== null &&
    pullRequest === null &&
    gitStatus.pullRequestAvailability === 'available';
  // D24: Discard file and Create PR under the diff (the pull request, once it exists).
  const footerActions = (path: string): ReactNode => (
    <>
      {reviewSource === 'changes' && (
        <Button onClick={() => fileAction(path, 'revert')} size="sm" variant="outline">
          <Undo2 />
          Discard file
        </Button>
      )}
      {pullRequest !== null && onOpenPullRequest !== undefined ? (
        <Button className="rounded-control" onClick={onOpenPullRequest} size="sm">
          <GitPullRequest />
          Pull request
        </Button>
      ) : (
        canCreatePullRequest && (
          <Button
            onClick={() =>
              setCreating({
                workspace,
                branch: gitStatus.branch,
                branches: gitStatus.branches,
                hasChanges: gitStatus.hasChanges,
                draft: draftPullRequests,
                conversationFiles,
              })
            }
            size="sm"
          >
            Create PR
          </Button>
        )
      )}
    </>
  );
  // The docked panel's footer (D24), and a phone's under its one file (mobile 21).
  const diffLayout = { columns, footerActions };
  const cockpit = pullRequestCockpit;
  const reviewThreads = cockpit === null ? [] : diffReviewThreads(cockpit);
  const threadActions =
    cockpit === null
      ? {}
      : {
          onReplyThread: (threadId: string, body: string) =>
            onMutatePullRequest({
              action: 'reply_thread',
              body,
              expectedHeadSha: cockpit.headSha,
              threadId,
            }),
          onResolveThread: (threadId: string) =>
            onMutatePullRequest({
              action: 'resolve_thread',
              expectedHeadSha: cockpit.headSha,
              threadId,
            }),
        };
  return (
    <div className="desktop-panel-view-enter flex h-full min-h-0 flex-col">
      {scopable && (
        <div className="flex shrink-0 justify-center px-2 pt-2" data-review-scope>
          <SegmentedControl
            aria-label="Changes of"
            onValueChange={(value) => setScope(value === 'folder' ? 'folder' : 'conversation')}
            value={scope}
          >
            <SegmentedControlItem value="conversation">This conversation</SegmentedControlItem>
            <SegmentedControlItem value="folder">Whole folder</SegmentedControlItem>
          </SegmentedControl>
        </div>
      )}
      <div className="min-h-0 flex-1">
        {content.kind === 'override' ? (
          <DesktopGitDiff
            {...diffLayout}
            label={reviewLabel}
            onSubmitReview={onSubmitReview}
            onToggleSource={toggleSource}
            reviewThreads={reviewThreads}
            {...threadActions}
            value={content.diff}
          />
        ) : content.kind === 'error' ? (
          <p className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-destructive text-xs">
            {content.error}
          </p>
        ) : content.kind === 'clean' ? (
          scopedToConversation ? (
            <PanelEmpty
              icon={Check}
              title="No changes of this conversation"
              text="The files it changed are as committed. Other changes in the folder are under Whole folder."
            />
          ) : (
            <PanelEmpty
              icon={Check}
              title="Working tree clean"
              text="Git reports no staged, modified, or untracked files."
            />
          )
        ) : content.kind === 'diff' ? (
          <DesktopGitDiff
            {...diffLayout}
            label={reviewLabel}
            onFileAction={reviewSource === 'changes' ? fileAction : undefined}
            onSubmitReview={onSubmitReview}
            onToggleSource={toggleSource}
            reviewThreads={reviewSource === 'pull-request' ? reviewThreads : []}
            {...threadActions}
            value={content.diff}
          />
        ) : (
          <PanelEmpty
            icon={FileCode2}
            title={
              reviewSource === 'pull-request'
                ? 'Pull request has no diff'
                : `${changedCount} changed ${changedCount === 1 ? 'file' : 'files'}`
            }
            text={
              reviewSource === 'pull-request'
                ? 'The branch currently matches its base branch.'
                : 'Git did not return a text diff for these changes.'
            }
          />
        )}
      </div>
      {creating !== null && (
        <CreatePullRequestDialog
          generateText={generateGitText}
          input={creating}
          onClose={(result) => {
            setCreating(null);
            if (result?.pullRequestUrl)
              window.open(result.pullRequestUrl, '_blank', 'noopener,noreferrer');
            if (result) void onPullRequestChange?.();
          }}
          onRefresh={onPullRequestChange}
        />
      )}
    </div>
  );
}

function diffReviewThreads(cockpit: PullRequestCockpit): PullRequestReviewThread[] {
  const feedback = visiblePullRequestFeedback(cockpit);
  const inlineFallbacks = feedback.standalone
    .filter(
      (comment) => comment.kind === 'inline' && comment.path !== null && comment.line !== null,
    )
    .map((comment) => ({
      id: `feedback:${comment.id}`,
      path: comment.path ?? '',
      line: comment.line,
      startLine: null,
      side: 'right' as const,
      isResolved: false,
      isOutdated: false,
      viewerCanResolve: false,
      viewerCanUnresolve: false,
      comments: [{ ...comment, kind: 'inline' as const, replyToId: null }],
    }));
  return [...feedback.threads, ...inlineFallbacks];
}
